import { useCallback, useEffect, useRef, useState } from 'react';

import {
  appleLoginRequest,
  googleLoginRequest,
} from '../../redux/actions/authActions';
import {
  consumeOAuthRedirect,
  extractOAuthCredential,
  pendingOAuthRedirect,
  signInWithApple,
  signInWithGoogle,
} from '../../utils/firebase.utils';

import { useAuth } from './useAuth';

const PROVIDER_LABELS = { google: 'Google', apple: 'Apple' };

// Popups the user dismissed themselves, and the duplicate-request error the SDK
// raises when a second popup supersedes the first. Neither is worth an error
// message — the user knows they closed it.
const CANCELLED_CODES = [
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
];

/**
 * Google and Apple sign-in for the auth pages.
 *
 * Desktop resolves inline through a popup. Mobile leaves the page entirely and
 * comes back, so the completion path has to run on mount too — both routes
 * funnel into `completeSignIn`. Because the provider returns to whichever page
 * started the flow, every page offering social sign-in needs this hook.
 *
 * @param {(message: string) => void} [onError] shows a message to the user
 * @returns {{ signInWithProvider: (method: 'google' | 'apple') => Promise<void>,
 *   isSocialLoading: boolean }}
 */
export const useSocialAuth = (onError) => {
  const { handleAuthSuccess } = useAuth();
  // Starts busy when the page was reached by returning from a provider, so the
  // login form doesn't flash before the redirect result resolves.
  const [isSocialLoading, setIsSocialLoading] = useState(
    () => !!pendingOAuthRedirect()
  );

  // Held in a ref so a page passing an inline callback doesn't re-run the
  // redirect effect on every render.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const fail = useCallback((method) => {
    setIsSocialLoading(false);
    const label = PROVIDER_LABELS[method] || 'Social';
    if (onErrorRef.current) onErrorRef.current(`${label} sign-in failed`);
  }, []);

  const completeSignIn = useCallback(
    async ({ method, credential }) => {
      if (!credential) {
        fail(method);
        return;
      }
      setIsSocialLoading(true);

      const onSuccess = (userData) => {
        // Clubbie's backend tags newly-created users with `showWizard: true`
        // (auth.service.js loginWithGoogle / loginWithApple) and never sets it
        // for existing ones. Preferred over Firebase's own isNewUser because a
        // Clubbie account can already exist for a first-time Firebase
        // credential.
        const isNewUser = userData?.user?.showWizard === true;
        handleAuthSuccess(userData, undefined, {
          loginMethod: method,
          isSignup: isNewUser,
          isNewUser,
        });
      };

      if (method === 'google') {
        await googleLoginRequest(credential.idToken, onSuccess, () =>
          fail(method)
        );
      } else {
        await appleLoginRequest(credential, onSuccess, () => fail(method));
      }
    },
    [fail, handleAuthSuccess]
  );

  const signInWithProvider = useCallback(
    async (method) => {
      setIsSocialLoading(true);
      try {
        const result =
          method === 'google'
            ? await signInWithGoogle()
            : await signInWithApple();

        // Mobile hands off to a full-page redirect and returns nothing — the
        // browser is on its way out, and the effect below picks the result up
        // when it comes back.
        if (!result) return;

        const outcome = extractOAuthCredential(result);
        if (!outcome) {
          fail(method);
          return;
        }
        await completeSignIn(outcome);
      } catch (error) {
        if (CANCELLED_CODES.includes(error?.code)) {
          setIsSocialLoading(false);
          return;
        }
        fail(method);
      }
    },
    [completeSignIn, fail]
  );

  // Resolve a redirect sign-in the browser has just come back from.
  useEffect(() => {
    let cancelled = false;

    const resolveRedirect = async () => {
      // Read before consuming — completing the flow clears it.
      const method = pendingOAuthRedirect();
      try {
        const outcome = await consumeOAuthRedirect();
        if (cancelled) return;
        if (!outcome) {
          setIsSocialLoading(false);
          return;
        }
        await completeSignIn(outcome);
      } catch (error) {
        if (!cancelled) fail(method);
      }
    };

    resolveRedirect();
    return () => {
      cancelled = true;
    };
  }, [completeSignIn, fail]);

  return { signInWithProvider, isSocialLoading };
};

export default useSocialAuth;
